import React from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import Input from "components/input";
import {
  INTERPRETATION_SERVICE_NAME,
  ONLINE_FULL_DAY_TITLE,
  DTP_L0_TITLE,
  DTP_SERVICE_NAME,
  LQE_REVIEW_SERVICE_NAME,
} from "utils/constantValues";

const RateTypesInputs = ({
  rateType,
  register,
  onHandleChange,
  serviceId,
  rateIndex,
  setValue,
  showDisclaimer,
  isCharacter = false,
}) => {
  const [t] = useTranslation();

  const isInterpretation = rateType.serviceName === INTERPRETATION_SERVICE_NAME;
  const isDtp = rateType.serviceName === DTP_SERVICE_NAME;

  const isOnlineFullDayRateId = ONLINE_FULL_DAY_TITLE === rateType.title;
  if (isDtp && rateType.title !== DTP_L0_TITLE) {
    return null;
  }

const ratePlaceholderKey = isDtp 
  ? "rate-per slide L0" 
  : `rate-${rateType.title}`;

const isLqeReview = rateType.serviceName === LQE_REVIEW_SERVICE_NAME;
const capacityPlaceholderKey = isDtp
  ? "Capacity-per slide L0"
  : isLqeReview
  ? `${rateType.serviceName}-${rateType.title}`
  : `${rateType.title}`;



  const showError = (isRate) => {
    const rateString = isRate
      ? String(rateType.rate)
      : String(rateType.capacity);
    const decimalPart = rateString.split(".")[1] || "";

    if (isInterpretation) {
      if (!isOnlineFullDayRateId) {
        return { isValid: true, message: "" };
      }
    }
    if (!rateString || parseFloat(rateString) === 0) {
      return { isValid: false, message: "emptyOrZero" };
    }

    if (isRate && decimalPart.length > 3) {
      return { isValid: false, message: "rateValueError" };
    }
    if (
      !isRate &&
      rateType?.CsLinguistSupportedServices &&
      !Number.isInteger(parseFloat(rateType.capacity))
    ) {
      return { isValid: false, message: "mustBeInteger" };
    }

    return { isValid: true, message: "" };
  };

  const validationCapacityResult = showError(false);
  const errorMessageForCapacity = !validationCapacityResult.isValid
    ? t(validationCapacityResult.message)
    : "";

  const validationRateResult = showError(true);
  const errorMessageForRates = !validationRateResult.isValid
    ? t(validationRateResult.message)
    : "";

  return (
    <div className="grid gap-[10px] w-full">
      {rateType.withRate && (
        <div className="text-gray-500 font-regular text-[14px] not-italic mt-[10px] ">
          {isInterpretation
            ? t(`subTitle-${rateType.serviceName}-${rateType.title}`)
            : t(`subTitle-${rateType.title}`, {
                isCharacter: isCharacter ? t("character") : t("word"),
              })}
        </div>
      )}


      {rateType.withRate && showDisclaimer && (
        <div className="text-red font-bold text-[14px] not-italic mt-[10px] ">
          {t("rateDisclaimer")}
        </div>
      )}

      {rateType.withRate && (
        <div
          className={`grid ${
            rateType.withCapacity ? "grid-cols-2" : "grid-cols-1"
          } sm-max:grid-cols-1 gap-[10px]`}
        >
          <Input
            value={rateType.rate || null}
            placeholder={t(ratePlaceholderKey, {
            isCharacter: isCharacter ? t("character") : t("word"),
          })}
            onChange={(e) => {
              const newRate = e.target.value;
              setValue(`rate-${rateIndex}-${rateType.rateId}`, newRate);
              onHandleChange(newRate, serviceId, "rate", rateIndex, setValue);
            }}
            label=""
            inputMode="numeric"
            type="number"
            name={`rate-${rateIndex}-${rateType.rateId}`}
            register={register}
            registerArgs={{
              required: isInterpretation
                ? isOnlineFullDayRateId
                  ? t("errorField", {
                      field: t("rate"),
                    })
                  : false
                : t("errorField", {
                    field: t("rate"),
                  }),
              validate: (value) => {
                if (validationRateResult.isValid) {
                  return true;
                } else {
                  return t(validationRateResult.message);
                }
              },
            }}
            min={0}
            step={0.001}
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] w-full sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              validationRateResult.isValid
                ? "border-default-color"
                : "border-red"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal placeholder:text-gray-500 py-[16px] px-[14px]`}
            isRequired={!validationRateResult.isValid}
            errorMessage={errorMessageForRates}
          />

          {rateType.withCapacity && (
            <Input
              value={rateType.capacity || null}
              placeholder={t(capacityPlaceholderKey, {
              isCharacter: isCharacter
                ? t("character").toLowerCase()
                : t("word").toLowerCase(),
            })}
              min={0}
              onChange={(e) => {
                const newCapacity = e.target.value;

                setValue(
                  `capacity-${rateIndex}-${rateType.rateId}`,
                  newCapacity
                );
                onHandleChange(
                  newCapacity,
                  serviceId,
                  "capacity",
                  rateIndex,
                  setValue
                );
              }}
              register={register}
              registerArgs={{
                required: t("errorField", {
                  field: t("capacity"),
                }),
                validate: (value) => {
                  if (validationCapacityResult.isValid) {
                    return true;
                  } else {
                    return t(validationCapacityResult.message);
                  }
                },
              }}
              label=""
              inputMode="numeric"
              type="number"
              name={`capacity-${rateIndex}-${rateType.rateId}`}
              labelClassName="text-primary font-semibold text-[16px]"
              inputClassName={`flex h-[43px] w-full sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                validationCapacityResult.isValid
                  ? "border-default-color"
                  : "border-red"
              } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px] `}
              isRequired={!validationCapacityResult.isValid}
              errorMessage={errorMessageForCapacity}
            />
          )}
        </div>
      )}
    </div>
  );
};

RateTypesInputs.propTypes = {
  rateType: PropTypes.object,
  onHandleChange: PropTypes.func,
  register: PropTypes.func,
  serviceId: PropTypes.string,
  rateIndex: PropTypes.number,
  setValue: PropTypes.func,
  showDisclaimer: PropTypes.bool,
  isCharacter: PropTypes.bool,
};

export default RateTypesInputs;
 